<template>
  <div class="admin-shell">
    <header class="admin-topbar">
      <div class="admin-brand">
        <span class="admin-eyebrow">管理后台</span>
        <h1>管理后台</h1>
      </div>
      <div class="admin-topbar__actions">
        <span class="admin-user">● {{ userStore.realName || '管理员' }}</span>
        <button type="button" class="admin-logout" @click="handleLogout">退出</button>
      </div>
    </header>

    <nav class="admin-tabs">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        class="admin-tab"
        :class="{ 'is-active': activeTab === tab.key }"
        @click="activeTab = tab.key"
      >
        <span class="admin-tab__icon" v-html="tab.icon"></span>
        <span>{{ tab.label }}</span>
      </button>
    </nav>

    <main class="admin-main">
      <section v-if="activeTab === 'upload'" class="admin-section">
        <div class="admin-section__header">
          <div>
            <span class="admin-section__eyebrow">— 数据导入</span>
            <h2>Excel 上传 & 营期设置</h2>
          </div>
        </div>

        <div class="admin-grid admin-grid--2col">
          <div class="admin-card">
            <h3>上传 Excel</h3>
            <p class="admin-card__desc">解析分配数据后，选择已有营期执行整批导入；新导入会覆盖该营期当前分配记录，但保留导入历史</p>
            <div
              class="upload-zone"
                :class="{ 'is-dragover': dragOver }"
                role="button"
                tabindex="0"
                @dragover.prevent="dragOver = true"
                @dragleave="dragOver = false"
                @drop.prevent="handleDrop"
                @click="triggerFileInput"
                @keydown.enter.prevent="triggerFileInput"
                @keydown.space.prevent="triggerFileInput"
              >
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              <span>拖拽文件到此处或点击上传</span>
              <span class="upload-zone__hint">支持 .xlsx / .xls，最大 10MB</span>
            </div>
            <input ref="fileInput" type="file" accept=".xlsx,.xls" hidden @change="handleFileChange" />
            <div v-if="uploadedFile && uploadStep === 'idle'" class="upload-result">
              <span class="upload-result__name">{{ uploadedFile.name }}</span>
              <span class="upload-result__size">{{ (uploadedFile.size / 1024).toFixed(1) }} KB</span>
              <button type="button" class="admin-btn admin-btn--sm" @click="parseExcel">解析文件</button>
              </div>
              <div v-if="uploadStep === 'parsing'" class="upload-result">
                <span class="admin-text-muted">解析中...</span>
              </div>
              <div v-if="uploadStep === 'preview' && parsedData" class="upload-result">
                <span class="upload-result__name">{{ parsedData.fileName }}</span>
                <span class="admin-text-success">{{ importablePreviewCount }} 条可导入</span>
                <span v-if="parsedData.fail" class="admin-text-warning">{{ parsedData.fail }} 条失败</span>
                <span v-if="parsedData.orgMappingFailCount" class="admin-text-danger">{{ parsedData.orgMappingFailCount }} 条组织未匹配</span>
                <div class="admin-field admin-field--import-target">
                <label class="admin-field__label">导入到营期</label>
                <select v-model="selectedImportCampPeriodId" class="admin-field__input">
                  <option value="" disabled>请选择已有营期</option>
                  <option v-for="period in campPeriods" :key="period.id" :value="period.id">
                    {{ period.name }}
                  </option>
                </select>
              </div>
                <button type="button" class="admin-btn admin-btn--sm" :disabled="importCompleted || importablePreviewCount === 0" @click="confirmSave">
                  {{ importCompleted ? '已导入' : '确认导入' }}
                </button>
                <button type="button" class="admin-btn admin-btn--sm admin-btn--ghost" @click="resetUpload">{{ importCompleted ? '重新上传' : '取消' }}</button>
            </div>
            <div v-if="uploadStep === 'preview' && parsedData && failedPreviewRows.length" class="upload-failures">
              <div class="upload-failures__header">
                <div>
                  <h4>未匹配明细</h4>
                  <span>{{ failedPreviewRows.length }} 条不会导入，其余已匹配记录可正常导入</span>
                </div>
                  <button type="button" class="admin-link-btn" @click="showFailureDetails = !showFailureDetails">
                  {{ showFailureDetails ? '收起明细' : '展开明细' }}
                </button>
              </div>
              <div class="upload-failure-summary">
                <span
                  v-for="item in failureSummary"
                  :key="item.reason"
                  class="upload-failure-summary__pill"
                >
                  {{ item.reason }} <strong>{{ item.count }}</strong>
                </span>
              </div>
                <p class="admin-card__desc admin-card__desc--spaced">
                组织未匹配的记录会被跳过并保存在导入历史里；家长手机号匹配不到客户不会拦导入，只会在导入完成后计入“未关联客户”。
              </p>
              <div v-if="showFailureDetails" class="upload-failures__table-wrap">
                <table class="admin-table upload-failures__table">
                  <thead>
                    <tr>
                      <th>交付去向</th>
                      <th>对接经理</th>
                      <th>对接团队</th>
                      <th>对接老师</th>
                      <th>家长姓名</th>
                      <th>家长电话</th>
                      <th>失败原因</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, index) in failedPreviewRows" :key="`${row.deliveryTarget || ''}-${row.parentPhone || ''}-${index}`">
                      <td>{{ row.deliveryTarget || '-' }}</td>
                      <td>{{ row.managerName || '-' }}</td>
                      <td>{{ row.teamName || '-' }}</td>
                      <td>{{ row.teacherName || '-' }}</td>
                      <td>{{ row.parentName || '-' }}</td>
                      <td>{{ row.parentPhone || '-' }}</td>
                      <td class="upload-failures__reason">{{ row.orgMappingError || '组织映射失败' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              </div>
              <div v-if="uploadStep === 'saving'" class="upload-result">
                <span class="admin-text-muted">保存中...</span>
              </div>
              <p v-if="uploadMessage" :class="['admin-card__desc', 'admin-message', uploadMessageType === 'error' ? 'admin-message--error' : 'admin-message--success']">
              {{ uploadMessage }}
            </p>
          </div>

          <div class="admin-card">
            <h3>新增空营期</h3>
            <p class="admin-card__desc">先创建可选营期，再将 Excel 导入到对应营期，避免“只建营期 / 误导入”混在同一步里</p>
            <div class="admin-form">
              <div class="admin-field">
                <label class="admin-field__label">营期名称</label>
                <input v-model="campPeriodForm.name" type="text" class="admin-field__input" placeholder="如：2026 春季营" />
              </div>
              <div class="admin-field-row">
                <div class="admin-field">
                  <label class="admin-field__label">开始日期</label>
                  <input v-model="campPeriodForm.startDate" type="date" class="admin-field__input" />
                </div>
                <div class="admin-field">
                  <label class="admin-field__label">结束日期</label>
                  <input v-model="campPeriodForm.endDate" type="date" class="admin-field__input" />
                </div>
              </div>
              <div class="admin-field">
                <label class="admin-field__label">状态</label>
                <select v-model="campPeriodForm.status" class="admin-field__input">
                  <option value="ACTIVE">进行中</option>
                  <option value="COMPLETED">已结束</option>
                </select>
              </div>
                <button type="button" class="admin-btn" @click="saveCampPeriod">创建营期</button>
                <p v-if="campPeriodMessage" :class="['admin-card__desc', 'admin-message', campPeriodMessageType === 'error' ? 'admin-message--error' : 'admin-message--success']">
                {{ campPeriodMessage }}
              </p>
            </div>
          </div>
        </div>

          <div class="admin-card admin-card--stacked">
            <div class="admin-section__header admin-section__header--compact">
              <h3>营期列表</h3>
              <button type="button" class="admin-btn admin-btn--sm" @click="openCampPeriodForm(null)">+ 新增营期</button>
          </div>
          <table class="admin-table">
            <thead>
              <tr>
                <th>营期名称</th>
                <th>开始日期</th>
                <th>结束日期</th>
                <th>状态</th>
                <th>创建时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
                <tr v-if="campPeriodLoading"><td colspan="6" class="admin-table__loading">加载中...</td></tr>
              <tr v-if="!campPeriodLoading && campPeriods.length === 0"><td colspan="6" class="center-members__empty">暂无营期数据</td></tr>
              <tr v-for="period in campPeriods" :key="period.id">
                <td><strong>{{ period.name }}</strong></td>
                <td>{{ period.startDate }}</td>
                <td>{{ period.endDate }}</td>
                <td><span class="admin-tag" :class="period.status === 'ACTIVE' ? 'admin-tag--success' : 'admin-tag--muted'">{{ period.status === 'ACTIVE' ? '进行中' : '已结束' }}</span></td>
                <td>{{ formatDate(period.createdAt) }}</td>
                <td>
                  <div class="admin-row-actions">
                      <button type="button" class="admin-link-btn" @click="openCampPeriodForm(period)">编辑</button>
                      <button type="button" class="admin-link-btn admin-link-btn--danger" @click="removeCampPeriod(period.id)">删除</button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

          <div class="admin-card admin-card--stacked">
          <h3>导入历史</h3>
          <table class="admin-table">
            <thead>
              <tr>
                <th>营期</th>
                <th>文件名</th>
                <th>导入时间</th>
                <th>记录数</th>
                <th>组织映射</th>
                <th>客户关联</th>
                <th>状态</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
                <tr v-if="importLoading"><td colspan="8" class="admin-table__loading">加载中...</td></tr>
              <tr v-if="!importLoading && importRecords.length === 0"><td colspan="8" class="center-members__empty">暂无导入记录</td></tr>
              <tr v-for="record in importRecords" :key="record.id" :class="{ 'admin-table__row--fresh': record.id === latestImportRecordId }">
                <td>{{ record.campPeriodName }}</td>
                <td>{{ record.fileName }}</td>
                <td>{{ formatDate(record.importTime) }}</td>
                <td>{{ record.recordCount }}</td>
                <td>
                  <span class="admin-tag" :class="record.orgMappingFailCount ? 'admin-tag--warn' : 'admin-tag--success'">
                    {{ record.orgMappingFailCount ? `${record.orgMappingFailCount} 条失败` : '全部命中' }}
                  </span>
                </td>
                <td>
                  <span class="admin-tag" :class="record.customerUnmatchedCount ? 'admin-tag--warn' : 'admin-tag--success'">
                    {{ record.customerUnmatchedCount ? `${record.customerUnmatchedCount} 条未关联` : '全部关联' }}
                  </span>
                </td>
                <td><span class="admin-tag" :class="record.status === 'SUCCESS' ? 'admin-tag--success' : 'admin-tag--warn'">{{ record.status === 'SUCCESS' ? '导入成功' : '部分失败' }}</span></td>
                <td>
                    <button v-if="(record.status === 'PARTIAL_FAIL' && record.failDetail) || record.customerUnmatchedDetail" type="button" class="admin-link-btn" @click="openImportDetail(record)">查看详情</button>
                    <span v-else class="admin-muted-dash">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="showCampPeriodForm" class="admin-modal-overlay" @click.self="showCampPeriodForm = false">
          <div class="admin-modal">
            <div class="admin-modal__header">
              <h3>{{ editingCampPeriod ? '编辑营期' : '新增营期' }}</h3>
              <button type="button" class="admin-modal__close" aria-label="关闭弹窗" @click="showCampPeriodForm = false">&#10005;</button>
            </div>
            <div class="admin-form">
              <div class="admin-field">
                <label class="admin-field__label">营期名称</label>
                <input v-model="campPeriodForm.name" type="text" class="admin-field__input" placeholder="如：2026 春季营" />
              </div>
              <div class="admin-field-row">
                <div class="admin-field">
                  <label class="admin-field__label">开始日期</label>
                  <input v-model="campPeriodForm.startDate" type="date" class="admin-field__input" />
                </div>
                <div class="admin-field">
                  <label class="admin-field__label">结束日期</label>
                  <input v-model="campPeriodForm.endDate" type="date" class="admin-field__input" />
                </div>
              </div>
              <div class="admin-field">
                <label class="admin-field__label">状态</label>
                <select v-model="campPeriodForm.status" class="admin-field__input">
                  <option value="ACTIVE">进行中</option>
                  <option value="COMPLETED">已结束</option>
                </select>
              </div>
              <div class="admin-modal__footer">
                <button type="button" class="admin-btn admin-btn--ghost" @click="showCampPeriodForm = false">取消</button>
                <button type="button" class="admin-btn" @click="submitCampPeriod">保存</button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="showImportDetail" class="admin-modal-overlay" @click.self="closeImportDetail">
          <div class="admin-modal admin-modal--wide">
            <div class="admin-modal__header">
              <div>
                <h3>导入详情</h3>
                <p class="admin-modal__sub">{{ selectedImportRecord?.fileName }} · {{ selectedImportRecord?.campPeriodName }}</p>
              </div>
              <button type="button" class="admin-modal__close" aria-label="关闭弹窗" @click="closeImportDetail">&#10005;</button>
            </div>
            <div class="import-detail-summary">
              <div><span>记录数</span><strong>{{ selectedImportRecord?.recordCount || 0 }}</strong></div>
              <div><span>组织失败</span><strong>{{ selectedImportRecord?.orgMappingFailCount || 0 }}</strong></div>
              <div><span>客户未关联</span><strong>{{ selectedImportRecord?.customerUnmatchedCount || 0 }}</strong></div>
              <div><span>状态</span><strong>{{ selectedImportRecord?.status === 'SUCCESS' ? '导入成功' : '部分失败' }}</strong></div>
            </div>
            <div v-if="parsedImportDetailRows.length" class="upload-failure-summary upload-failure-summary--modal">
              <span v-for="item in importDetailSummary" :key="item.reason" class="upload-failure-summary__pill">
                {{ item.reason }} <strong>{{ item.count }}</strong>
              </span>
            </div>
            <div v-if="parsedImportDetailRows.length" class="upload-failures__table-wrap import-detail-table">
              <h4 style="margin: 0 0 8px; font-size: 14px; color: #666;">组织映射失败明细</h4>
              <table class="admin-table upload-failures__table">
                <thead>
                  <tr>
                    <th>交付去向</th>
                    <th>对接经理</th>
                    <th>对接团队</th>
                    <th>对接老师</th>
                    <th>家长姓名</th>
                    <th>家长电话</th>
                    <th>失败原因</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in parsedImportDetailRows" :key="`detail-${index}`">
                    <td>{{ row.deliveryTarget || '-' }}</td>
                    <td>{{ row.managerName || '-' }}</td>
                    <td>{{ row.teamName || '-' }}</td>
                    <td>{{ row.teacherName || '-' }}</td>
                    <td>{{ row.parentName || '-' }}</td>
                    <td>{{ row.parentPhone || '-' }}</td>
                    <td class="upload-failures__reason">{{ row.orgMappingError || row.reason || row.raw || '组织映射失败' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="parsedCustomerUnmatchedRows.length" class="upload-failures__table-wrap import-detail-table" style="margin-top: 16px;">
              <h4 style="margin: 0 0 8px; font-size: 14px; color: #666;">未关联客户明细</h4>
              <table class="admin-table upload-failures__table">
                <thead>
                  <tr>
                    <th>家长姓名</th>
                    <th>家长电话</th>
                    <th>对接老师</th>
                    <th>对接经理</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in parsedCustomerUnmatchedRows" :key="`unmatched-${index}`">
                    <td>{{ row.parentName || '-' }}</td>
                    <td>{{ row.parentPhone || '-' }}</td>
                    <td>{{ row.teacherName || '-' }}</td>
                    <td>{{ row.managerName || '-' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="!parsedImportDetailRows.length && !parsedCustomerUnmatchedRows.length" class="center-members__empty">
              该导入记录生成于旧版本，未保存可展示的明细。
            </div>
          </div>
        </div>

        <div v-if="pendingDeleteCampPeriodId" class="admin-modal-overlay" @click.self="closeDeleteCampPeriodConfirm">
          <div class="admin-modal admin-modal--confirm">
            <div class="admin-modal__header">
              <h3>删除营期</h3>
              <button type="button" class="admin-modal__close" aria-label="关闭弹窗" @click="closeDeleteCampPeriodConfirm">&#10005;</button>
            </div>
            <p class="admin-confirm__text">删除营期将同时删除该营期下所有交付记录和导入历史，此操作不可撤销。</p>
            <div class="admin-modal__footer">
              <button type="button" class="admin-btn admin-btn--ghost" @click="closeDeleteCampPeriodConfirm">取消</button>
              <button type="button" class="admin-btn admin-btn--danger" @click="confirmDeleteCampPeriod">确认删除</button>
            </div>
          </div>
        </div>
      </section>

      <section v-if="activeTab === 'centers'" class="admin-section">
        <div class="admin-section__header">
          <div>
            <span class="admin-section__eyebrow">— 组织管理</span>
            <h2>组织架构</h2>
          </div>
          <div class="admin-section__tools">
            <button type="button" class="admin-btn admin-btn--ghost" @click="fetchCenters">刷新列表</button>
          </div>
        </div>

        <div class="admin-notice">
          <strong>只读模式</strong>
          <span>组织架构由企微同步维护，本页不支持手工新增、编辑或删除。</span>
        </div>

        <div v-if="centers.length === 0" class="admin-card">
          <div class="center-members__empty">暂无组织架构数据</div>
        </div>

        <div v-for="center in centers" :key="center.id" class="admin-card org-card">
          <div class="center-header">
            <button type="button" class="center-header__left" @click="toggleCenter(center.id)">
              <svg class="collapse-arrow" :class="{ 'is-collapsed': isCenterCollapsed(center.id) }" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              <h3>{{ center.name }}</h3>
              <span v-if="orgLeaderName(center)" class="admin-tag admin-tag--info">负责人：{{ orgLeaderName(center) }}</span>
              <span class="admin-tag admin-tag--muted">{{ countGroups(center) }} 个小组</span>
              <span class="admin-tag admin-tag--muted">{{ countMembers(center) }} 人</span>
            </button>
            <span class="admin-tag admin-tag--success">只读</span>
          </div>

          <div v-show="!isCenterCollapsed(center.id)">
            <template v-if="orgChildren(center).length">
              <div v-for="child in orgChildren(center)" :key="child.id" class="group-section" :class="{ 'dept-section': child.type === 'DEPT' }">
                <template v-if="child.type === 'DEPT'">
                  <div class="dept-header">
                    <div>
                      <strong>{{ child.name }}</strong>
                      <span v-if="orgLeaderName(child)" class="admin-tag admin-tag--info">负责人：{{ orgLeaderName(child) }}</span>
                    </div>
                    <span class="admin-tag admin-tag--muted">{{ countGroups(child) }} 个小组</span>
                  </div>
                  <div v-if="orgChildren(child).length">
                    <div v-for="group in orgChildren(child)" :key="group.id" class="group-section group-section--nested">
                      <div class="group-header">
                        <button type="button" class="group-header__left" @click="toggleGroup(group.id)">
                          <svg class="collapse-arrow" :class="{ 'is-collapsed': isGroupCollapsed(group.id) }" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                          <span class="group-name">{{ group.name }}</span>
                          <span v-if="orgLeaderName(group)" class="admin-tag admin-tag--info">组长：{{ orgLeaderName(group) }}</span>
                          <span class="admin-tag admin-tag--muted">{{ orgMembers(group).length }} 人</span>
                        </button>
                      </div>
                      <div v-show="!isGroupCollapsed(group.id)" class="center-members">
                        <div v-for="member in orgMembers(group)" :key="member.userId || member.name" class="center-member">
                          <div class="center-member__info">
                            <strong>{{ member.name }}</strong>
                            <span>{{ member.position || '成员' }} · {{ member.rank || '-' }}</span>
                          </div>
                        </div>
                        <div v-if="!orgMembers(group).length" class="center-members__empty">暂无成员</div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="center-members__empty">暂无小组数据</div>
                </template>

                <template v-else>
                  <div class="group-header">
                    <button type="button" class="group-header__left" @click="toggleGroup(child.id)">
                      <svg class="collapse-arrow" :class="{ 'is-collapsed': isGroupCollapsed(child.id) }" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                      <span class="group-name">{{ child.name }}</span>
                      <span v-if="orgLeaderName(child)" class="admin-tag admin-tag--info">组长：{{ orgLeaderName(child) }}</span>
                      <span class="admin-tag admin-tag--muted">{{ orgMembers(child).length }} 人</span>
                    </button>
                  </div>
                  <div v-show="!isGroupCollapsed(child.id)" class="center-members">
                    <div v-for="member in orgMembers(child)" :key="member.userId || member.name" class="center-member">
                      <div class="center-member__info">
                        <strong>{{ member.name }}</strong>
                        <span>{{ member.position || '成员' }} · {{ member.rank || '-' }}</span>
                      </div>
                    </div>
                    <div v-if="!orgMembers(child).length" class="center-members__empty">暂无成员</div>
                  </div>
                </template>
              </div>
            </template>
            <div v-else class="center-members__empty">暂无下级组织数据</div>
          </div>
        </div>
      </section>

      <section v-if="activeTab === 'accounts'" class="admin-section">
        <div class="admin-section__header">
          <div>
            <span class="admin-section__eyebrow">— 账号管理</span>
            <h2>账号增删改查</h2>
          </div>
          <div class="admin-section__tools">
            <div class="admin-search">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input v-model="accountSearch" type="text" placeholder="搜索姓名或账号..." class="admin-field__input admin-field__input--search" />
            </div>
            <button type="button" class="admin-btn" @click="openAccountForm()">+ 新增账号</button>
          </div>
        </div>

        <div class="admin-card">
          <table class="admin-table">
            <thead>
              <tr>
                <th>姓名</th>
                <th>账号</th>
                <th>状态</th>
                <th>创建时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="account in filteredAccounts" :key="account.id">
                <td><strong>{{ account.realName }}</strong></td>
                <td>{{ account.username }}</td>
                <td>
                  <span class="admin-tag" :class="account.enabled ? 'admin-tag--success' : 'admin-tag--muted'">
                    {{ account.enabled ? '启用' : '停用' }}
                  </span>
                </td>
                <td>{{ formatDate(account.createdAt) }}</td>
                <td>
                  <div class="admin-row-actions">
                    <button type="button" class="admin-link-btn" @click="openAccountForm(account)">编辑</button>
                    <button type="button" class="admin-link-btn" @click="toggleAccount(account)">{{ account.enabled ? '停用' : '启用' }}</button>
                    <button type="button" class="admin-link-btn admin-link-btn--danger" @click="deleteAccount(account.id)">删除</button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-if="accountTotal > accountPageSize" class="admin-pagination">
            <button type="button" class="admin-btn admin-btn--sm admin-btn--ghost" :disabled="accountPage <= 1" @click="accountPage--">上一页</button>
            <span class="admin-pagination__info">第 {{ accountPage }} 页 / 共 {{ accountTotalPages }} 页 ({{ accountTotal }} 条)</span>
            <button type="button" class="admin-btn admin-btn--sm admin-btn--ghost" :disabled="accountPage >= accountTotalPages" @click="accountPage++">下一页</button>
          </div>
        </div>

        <div v-if="showAccountForm" class="admin-modal-overlay" @click.self="showAccountForm = false">
          <div class="admin-modal">
            <div class="admin-modal__header">
              <h3>{{ editingAccount ? '编辑账号' : '新增账号' }}</h3>
              <button type="button" class="admin-modal__close" aria-label="关闭弹窗" @click="showAccountForm = false">&#10005;</button>
            </div>
            <div class="admin-form">
              <div class="admin-field">
                <label class="admin-field__label">姓名</label>
                <input v-model="accountForm.realName" type="text" class="admin-field__input" placeholder="请输入姓名" />
              </div>
              <div class="admin-field">
                <label class="admin-field__label">账号</label>
                <input v-model="accountForm.username" type="text" class="admin-field__input" placeholder="请输入登录账号" :disabled="!!editingAccount" />
              </div>
              <div class="admin-field" v-if="!editingAccount">
                <label class="admin-field__label">密码</label>
                <input v-model="accountForm.password" type="password" class="admin-field__input" placeholder="请输入初始密码" />
              </div>
              <div class="admin-modal__footer">
                <button type="button" class="admin-btn admin-btn--ghost" @click="showAccountForm = false">取消</button>
                <button type="button" class="admin-btn" @click="saveAccount">{{ editingAccount ? '保存修改' : '创建账号' }}</button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { getOrgTree, getCenters, getGroupsByCenter, getUserList, addUser, deleteUser, addCampPeriod, updateCampPeriod, getCampPeriodList, deleteCampPeriod, parseCampExcel, saveCampPeriodData, getImportRecords } from '@/api'
import { useUserStore } from '@/store/user'
import { useRouter } from 'vue-router'

export default {
  name: 'AdminView',
  setup() {
    const userStore = useUserStore()
    const router = useRouter()
    return { userStore, router }
  },
  data() {
    return {
      activeTab: 'upload',
      tabs: [
        { key: 'upload', label: '数据导入', icon: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M17 8 12 3 7 8"/><path d="M12 3v12"/></svg>' },
        { key: 'centers', label: '分中心管理', icon: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/><path d="M9 9v.01"/><path d="M9 13v.01"/><path d="M9 17v.01"/></svg>' },
        { key: 'accounts', label: '账号管理', icon: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>' },
      ],
      dragOver: false,
      uploadedFile: null,
      uploadStep: 'idle',
      parsedData: null,
      selectedImportCampPeriodId: '',
      uploadMessage: '',
      uploadMessageType: 'success',
      showFailureDetails: false,
      importCompleted: false,
      campPeriodForm: {
        id: '',
        name: '',
        startDate: '',
        endDate: '',
        status: 'ACTIVE',
      },
      campPeriodMessage: '',
      campPeriodMessageType: 'success',
      campPeriods: [],
      campPeriodLoading: false,
      showCampPeriodForm: false,
      editingCampPeriod: null,
      importRecords: [],
      importLoading: false,
      latestImportRecordId: '',
      showImportDetail: false,
      selectedImportRecord: null,
      pendingDeleteCampPeriodId: '',
      centers: [],
      collapsedCenters: {},
      collapsedGroups: {},
      accountSearch: '',
      showAccountForm: false,
      editingAccount: null,
      accountForm: {
        userId: '',
        realName: '',
        username: '',
        password: '',
      },
      accounts: [],
      accountPage: 1,
      accountPageSize: 10,
      accountTotal: 0,
    };
  },
  mounted() {
    this.fetchCenters();
    this.fetchAccounts();
    this.fetchCampPeriods();
    this.fetchImportRecords();
  },
  computed: {
    importablePreviewCount() {
      const total = this.parsedData?.total || 0
      const failed = this.parsedData?.orgMappingFailCount || 0
      return Math.max(total - failed, 0)
    },
    failedPreviewRows() {
      return (this.parsedData?.rows || []).filter(row => row.orgMappingError)
    },
    failureSummary() {
      return this.groupFailureRows(this.failedPreviewRows)
    },
    parsedImportDetailRows() {
      return this.parseFailDetail(this.selectedImportRecord?.failDetail)
    },
    importDetailSummary() {
      return this.groupFailureRows(this.parsedImportDetailRows)
    },
    parsedCustomerUnmatchedRows() {
      const detail = this.selectedImportRecord?.customerUnmatchedDetail
      if (!detail) return []
      if (Array.isArray(detail)) return detail
      try {
        const parsed = typeof detail === 'string' ? JSON.parse(detail) : detail
        return Array.isArray(parsed) ? parsed : []
      } catch {
        return []
      }
    },
    filteredAccounts() {
      if (!this.accountSearch.trim()) return this.accounts;
      const q = this.accountSearch.toLowerCase();
      return this.accounts.filter(a => (a.realName || '').toLowerCase().includes(q) || a.username.toLowerCase().includes(q));
    },
    accountTotalPages() {
      return Math.ceil(this.accountTotal / this.accountPageSize);
    },
  },
  watch: {
    accountPage() {
      this.fetchAccounts();
    },
  },
  methods: {
    async fetchCenters() {
      try {
        const treeRes = await getOrgTree();
        if (treeRes.code === 200 && treeRes.data) {
          this.centers = this.normalizeOrgNodes(treeRes.data);
          this.collapseOrgTree();
          return;
        }
      } catch (err) {
        console.error('获取组织架构树失败，降级读取中心列表', err);
      }

      try {
        const res = await getCenters();
        if (res.code === 200 && res.data) {
          this.centers = res.data.map(c => ({ ...c, groups: [] }));
          const groupRequests = this.centers.map(async (center) => {
            try {
              const gRes = await getGroupsByCenter(center.id);
              if (gRes.code === 200 && gRes.data) {
                center.groups = gRes.data.map(g => ({
                  ...g,
                  members: g.members || []
                }));
              }
            } catch (e) {
              console.error(`获取 ${center.name} 小组失败`, e);
            }
          });
          await Promise.all(groupRequests);
          this.collapseOrgTree();
        }
      } catch (err) {
        console.error('获取中心列表失败', err);
      }
    },
    normalizeOrgNodes(nodes) {
      return (nodes || []).map(node => ({
        ...node,
        children: this.normalizeOrgNodes(node.children || []),
        members: node.members || []
      }));
    },
    collapseOrgTree() {
      const collapsedCenters = {};
      const collapsedGroups = {};
      const visit = (node, isRoot = false) => {
        if (!node || !node.id) return;
        if (isRoot) {
          collapsedCenters[node.id] = true;
        } else if (node.type === 'GROUP') {
          collapsedGroups[node.id] = true;
        }
        this.orgChildren(node).forEach(child => visit(child, false));
      };
      this.centers.forEach(center => visit(center, true));
      this.collapsedCenters = collapsedCenters;
      this.collapsedGroups = collapsedGroups;
    },
    handleLogout() {
      this.userStore.clearUser();
      this.router.push('/login');
    },
    async fetchAccounts() {
      try {
        const res = await getUserList({ page: this.accountPage, size: this.accountPageSize });
        if (res.code === 200 && res.data) {
          this.accounts = res.data.list || [];
          this.accountTotal = res.data.total || 0;
        }
      } catch (err) {
        console.error('获取账号列表失败', err);
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      const pad = n => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleFileChange(e) {
      const file = e.target.files[0];
      if (file) {
        this.uploadedFile = file;
        this.uploadStep = 'idle';
        this.parsedData = null;
        this.uploadMessage = '';
        this.showFailureDetails = false;
        this.importCompleted = false;
      }
    },
    handleDrop(e) {
      this.dragOver = false;
      const file = e.dataTransfer.files[0];
      if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
        this.uploadedFile = file;
        this.uploadStep = 'idle';
        this.parsedData = null;
        this.uploadMessage = '';
        this.showFailureDetails = false;
        this.importCompleted = false;
      }
    },
    async parseExcel() {
      if (!this.uploadedFile) return;
      this.uploadStep = 'parsing';
      this.uploadMessage = '';
      this.showFailureDetails = false;
      this.importCompleted = false;
      try {
        const res = await parseCampExcel(this.uploadedFile);
        if (res.code === 200 && res.data) {
          this.parsedData = res.data;
          this.uploadStep = 'preview';
          if (this.parsedData.orgMappingFailCount) {
            this.uploadMessage = `解析完成：${this.importablePreviewCount} 条可导入，${this.parsedData.orgMappingFailCount} 条组织未匹配将跳过。`;
            this.uploadMessageType = 'success';
          } else {
            this.uploadMessage = '解析完成，请选择营期后执行导入。';
            this.uploadMessageType = 'success';
          }
        } else {
          this.uploadStep = 'idle';
        }
      } catch (err) {
        console.error('解析Excel失败', err);
        this.uploadStep = 'idle';
        this.uploadMessage = err?.response?.data?.message || err?.message || '解析Excel失败';
        this.uploadMessageType = 'error';
      }
    },
    async confirmSave() {
      if (!this.parsedData) return;
      if (this.importCompleted) return;
      if (!this.selectedImportCampPeriodId) {
        this.uploadMessage = '请先选择要导入的营期';
        this.uploadMessageType = 'error';
        return;
      }
      if (this.importablePreviewCount === 0) {
        this.uploadMessage = '当前文件没有可导入记录，请调整组织映射后重新上传';
        this.uploadMessageType = 'error';
        return;
      }
      this.uploadStep = 'saving';
      this.uploadMessage = '';
      try {
        const res = await saveCampPeriodData({
          campPeriodId: this.selectedImportCampPeriodId,
          fileName: this.parsedData.fileName,
          records: this.parsedData.rows
        });
        if (res.code === 200) {
          const successCount = res.data?.success || 0
          const failCount = res.data?.fail || 0
          const unmatchedCustomerCount = res.data?.customerUnmatchedCount || 0
          this.uploadMessage = failCount
            ? `导入完成：成功 ${successCount} 条，失败 ${failCount} 条，未关联客户 ${unmatchedCustomerCount} 条。明细可在导入历史中查看。`
            : unmatchedCustomerCount
              ? `导入完成：成功 ${successCount} 条，未关联客户 ${unmatchedCustomerCount} 条。明细可在导入历史中查看。`
              : `导入完成：成功 ${successCount} 条。`
          this.uploadMessageType = failCount ? 'error' : 'success';
          this.uploadStep = 'preview';
          this.importCompleted = true;
          await this.fetchCampPeriods();
          await this.fetchImportRecords();
          this.latestImportRecordId = this.importRecords[0]?.id || '';
        } else {
          this.uploadStep = 'preview';
        }
      } catch (err) {
        console.error('保存失败', err);
        this.uploadStep = 'preview';
        this.uploadMessage = err?.response?.data?.message || err?.message || '导入失败';
        this.uploadMessageType = 'error';
      }
    },
    resetUpload() {
      this.uploadedFile = null;
      this.parsedData = null;
      this.uploadStep = 'idle';
      this.selectedImportCampPeriodId = '';
      this.showFailureDetails = false;
      this.importCompleted = false;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
    async saveCampPeriod() {
      if (!this.campPeriodForm.name.trim()) return;
      this.campPeriodMessage = '';
      try {
        if (this.campPeriodForm.id) {
          const res = await updateCampPeriod(this.campPeriodForm.id, {
            name: this.campPeriodForm.name,
            startDate: this.campPeriodForm.startDate,
            endDate: this.campPeriodForm.endDate,
            status: this.campPeriodForm.status
          });
          if (res.code === 200) {
            this.fetchCampPeriods();
            this.campPeriodMessage = '营期已更新';
            this.campPeriodMessageType = 'success';
          }
        } else {
          const res = await addCampPeriod({
            name: this.campPeriodForm.name,
            startDate: this.campPeriodForm.startDate,
            endDate: this.campPeriodForm.endDate,
            status: this.campPeriodForm.status
          });
          if (res.code === 200) {
            this.fetchCampPeriods();
            this.campPeriodForm = { id: '', name: '', startDate: '', endDate: '', status: 'ACTIVE' };
            this.selectedImportCampPeriodId = res.data?.id || '';
            this.campPeriodMessage = '营期已创建，可直接用于导入';
            this.campPeriodMessageType = 'success';
          }
        }
      } catch (err) {
        console.error('保存营期失败', err);
        this.campPeriodMessage = err?.response?.data?.message || err?.message || '保存营期失败';
        this.campPeriodMessageType = 'error';
      }
    },
    async fetchCampPeriods() {
      this.campPeriodLoading = true;
      try {
        const res = await getCampPeriodList();
        if (res.code === 200 && res.data) {
          this.campPeriods = res.data;
          if (!this.selectedImportCampPeriodId && this.campPeriods.length) {
            this.selectedImportCampPeriodId = this.campPeriods[0].id;
          }
        }
      } catch (err) {
        console.error('获取营期列表失败', err);
      } finally {
        this.campPeriodLoading = false;
      }
    },
    async fetchImportRecords() {
      this.importLoading = true;
      try {
        const res = await getImportRecords({});
        if (res.code === 200 && res.data) {
          this.importRecords = res.data;
        }
      } catch (err) {
        console.error('获取导入历史失败', err);
      } finally {
        this.importLoading = false;
      }
    },
    openImportDetail(record) {
      this.selectedImportRecord = record;
      this.showImportDetail = true;
    },
    closeImportDetail() {
      this.showImportDetail = false;
      this.selectedImportRecord = null;
    },
    groupFailureRows(rows) {
      const buckets = new Map();
      rows.forEach(row => {
        const reason = this.classifyFailureReason(row.orgMappingError || row.reason || row.raw || '');
        buckets.set(reason, (buckets.get(reason) || 0) + 1);
      });
      return [...buckets.entries()].map(([reason, count]) => ({ reason, count }));
    },
    classifyFailureReason(reason) {
      if (reason.includes('未找到中心')) return '未找到中心';
      if (reason.includes('未找到小组')) return '未找到小组';
      if (reason.includes('多个小组')) return '老师匹配多个小组';
      if (reason.includes('歧义') || reason.includes('不唯一')) return '组织匹配存在歧义';
      if (reason.includes('手机号') || reason.includes('客户')) return '手机号未关联客户';
      return reason ? '其他组织问题' : '组织映射失败';
    },
    parseFailDetail(detail) {
      if (!detail) return [];
      if (Array.isArray(detail)) return detail;
      if (typeof detail === 'object') return Array.isArray(detail.rows) ? detail.rows : [];
      try {
        const parsed = JSON.parse(detail);
        if (Array.isArray(parsed)) return parsed;
        if (parsed && Array.isArray(parsed.rows)) return parsed.rows;
      } catch (e) {
        // Plain-text fail details are parsed line by line below.
      }
      return String(detail)
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(Boolean)
        .map(line => this.parseFailDetailLine(line));
    },
    parseFailDetailLine(line) {
      const columns = line.split(/\t| {2,}/).map(part => part.trim()).filter(Boolean);
      if (columns.length >= 7) {
        return {
          deliveryTarget: columns[0],
          managerName: columns[1],
          teamName: columns[2],
          teacherName: columns[3],
          parentName: columns[4],
          parentPhone: columns[5],
          orgMappingError: columns.slice(6).join(' '),
          raw: line,
        };
      }
      return {
        orgMappingError: line,
        raw: line,
      };
    },
    openCampPeriodForm(period) {
      if (period) {
        this.editingCampPeriod = period;
        this.campPeriodForm = {
          id: period.id,
          name: period.name,
          startDate: period.startDate,
          endDate: period.endDate,
          status: period.status
        };
      } else {
        this.editingCampPeriod = null;
        this.campPeriodForm = { id: '', name: '', startDate: '', endDate: '', status: 'ACTIVE' };
      }
      this.campPeriodMessage = '';
      this.showCampPeriodForm = true;
    },
    async submitCampPeriod() {
      if (!this.campPeriodForm.name.trim()) return;
      try {
        if (this.editingCampPeriod) {
          const res = await updateCampPeriod(this.campPeriodForm.id, {
            name: this.campPeriodForm.name,
            startDate: this.campPeriodForm.startDate,
            endDate: this.campPeriodForm.endDate,
            status: this.campPeriodForm.status
          });
          if (res.code === 200) {
            this.showCampPeriodForm = false;
            this.campPeriodForm = { id: '', name: '', startDate: '', endDate: '', status: 'ACTIVE' };
            this.fetchCampPeriods();
          }
        } else {
          const res = await addCampPeriod({
            name: this.campPeriodForm.name,
            startDate: this.campPeriodForm.startDate,
            endDate: this.campPeriodForm.endDate,
            status: this.campPeriodForm.status
          });
          if (res.code === 200) {
            this.showCampPeriodForm = false;
            this.campPeriodForm = { id: '', name: '', startDate: '', endDate: '', status: 'ACTIVE' };
            this.fetchCampPeriods();
          }
        }
      } catch (err) {
        console.error('保存营期失败', err);
        this.campPeriodMessage = err?.response?.data?.message || err?.message || '保存营期失败';
        this.campPeriodMessageType = 'error';
      }
    },
    removeCampPeriod(id) {
      this.pendingDeleteCampPeriodId = id;
    },
    closeDeleteCampPeriodConfirm() {
      this.pendingDeleteCampPeriodId = '';
    },
    async confirmDeleteCampPeriod() {
      if (!this.pendingDeleteCampPeriodId) return;
      try {
        const res = await deleteCampPeriod(this.pendingDeleteCampPeriodId);
        if (res.code === 200) {
          this.pendingDeleteCampPeriodId = '';
          this.fetchCampPeriods();
          this.fetchImportRecords();
        }
      } catch (err) {
        console.error('删除营期失败', err);
      }
    },
    toggleCenter(id) {
      this.collapsedCenters[id] = !this.collapsedCenters[id];
    },
    toggleGroup(id) {
      this.collapsedGroups[id] = !this.collapsedGroups[id];
    },
    isCenterCollapsed(id) {
      return !!this.collapsedCenters[id];
    },
    isGroupCollapsed(id) {
      return !!this.collapsedGroups[id];
    },
    orgChildren(org) {
      return org.children || org.groups || [];
    },
    orgMembers(org) {
      return org.members || [];
    },
    orgLeaderName(org) {
      return org.leaderName || org.leader?.name || '';
    },
    countGroups(org) {
      const children = this.orgChildren(org);
      return children.reduce((sum, child) => {
        if (child.type === 'GROUP') return sum + 1;
        return sum + this.countGroups(child);
      }, 0);
    },
    countMembers(org) {
      return this.orgChildren(org).reduce((sum, child) => {
        const selfMembers = child.type === 'GROUP' ? this.orgMembers(child).length : 0;
        return sum + selfMembers + this.countMembers(child);
      }, 0);
    },
    openAccountForm(account) {
      if (account) {
        this.editingAccount = account;
        this.accountForm = { userId: account.userId || '', realName: account.realName, username: account.username, password: '' };
      } else {
        this.editingAccount = null;
        this.accountForm = { userId: '', realName: '', username: '', password: '' };
      }
      this.showAccountForm = true;
    },
    async saveAccount() {
      if (this.editingAccount) {
        Object.assign(this.editingAccount, { realName: this.accountForm.realName });
      } else {
        if (!this.accountForm.username.trim() || !this.accountForm.password) return;
        try {
          const res = await addUser({
            userId: this.accountForm.username,
            username: this.accountForm.username,
            password: this.accountForm.password,
            realName: this.accountForm.realName,
            role: '',
            enabled: true
          });
          if (res.code === 200) {
            this.fetchAccounts();
          }
        } catch (err) {
          console.error('新增账号失败', err);
        }
      }
      this.showAccountForm = false;
    },
    toggleAccount(account) {
      account.enabled = !account.enabled;
    },
    async deleteAccount(id) {
      try {
        const res = await deleteUser(id);
        if (res.code === 200) {
          this.accounts = this.accounts.filter(a => a.id !== id);
        }
      } catch (err) {
        console.error('删除账号失败', err);
      }
    },
  },
};
</script>

<style scoped>
.admin-shell {
  max-width: 1200px;
  margin: 0 auto;
  padding: 16px 16px 32px;
  font-family: "Aptos", "PingFang SC", "Microsoft YaHei UI", sans-serif;
  color: var(--ink);
}

.admin-topbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  border: 1px solid rgba(29, 47, 44, 0.08);
  background: var(--surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow);
  backdrop-filter: blur(12px);
}

.admin-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.68rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-soft);
}

.admin-eyebrow::before {
  content: "";
  width: 16px;
  height: 1px;
  background: rgba(29, 47, 44, 0.28);
}

.admin-topbar h1 {
  margin: 4px 0 0;
  font-size: 1.4rem;
  font-family: "Avenir Next", "PingFang SC", "Microsoft YaHei UI", sans-serif;
  letter-spacing: -0.03em;
}

.admin-topbar__actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.admin-user {
  font-size: 0.78rem;
  color: var(--ink-soft);
  font-weight: 500;
}

.admin-logout {
  padding: 6px 14px;
  border: 1px solid rgba(29, 47, 44, 0.12);
  border-radius: 999px;
  background: transparent;
  color: var(--ink-soft);
  font-size: 0.78rem;
  cursor: pointer;
  transition: all 160ms ease;
}

.admin-logout:hover {
  background: rgba(203, 97, 69, 0.1);
  border-color: rgba(203, 97, 69, 0.2);
  color: var(--danger);
}

.admin-tabs {
  display: flex;
  gap: 6px;
  margin-top: 12px;
  padding: 4px;
  border: 1px solid rgba(29, 47, 44, 0.08);
  background: rgba(255, 250, 244, 0.6);
  border-radius: var(--radius-md);
}

.admin-tab {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 10px 16px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: var(--ink-soft);
  font-size: 0.84rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 160ms ease;
}

.admin-tab__icon,
.admin-tab__icon svg {
  width: 16px;
  height: 16px;
  flex: 0 0 auto;
}

.admin-tab__icon svg {
  display: block;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.admin-tab:hover {
  background: rgba(41, 107, 115, 0.06);
}

.admin-tab.is-active {
  background: linear-gradient(135deg, rgba(41, 107, 115, 0.14), rgba(43, 139, 100, 0.18));
  color: var(--ink);
  font-weight: 600;
}

.admin-main {
  margin-top: 12px;
}

.admin-section__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 16px;
}

.admin-section__header--compact {
  align-items: center;
  margin-bottom: 12px;
}

.admin-section__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.68rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-soft);
}

.admin-section__eyebrow::before {
  content: "";
  width: 16px;
  height: 1px;
  background: rgba(29, 47, 44, 0.28);
}

.admin-section__header h2 {
  margin: 4px 0 0;
  font-size: 1.2rem;
  font-family: "Avenir Next", "PingFang SC", "Microsoft YaHei UI", sans-serif;
}

.admin-section__tools {
  display: flex;
  gap: 10px;
  align-items: center;
}

.admin-grid {
  display: grid;
  gap: 16px;
}

.admin-grid--2col {
  grid-template-columns: 1fr 1fr;
}

.admin-card {
  padding: 20px;
  border: 1px solid rgba(29, 47, 44, 0.08);
  background: var(--surface);
  border-radius: var(--radius-lg);
  box-shadow: 0 8px 24px rgba(37, 56, 50, 0.06);
}

.admin-card--stacked {
  margin-top: 16px;
}

.admin-card h3 {
  margin: 0 0 4px;
  font-size: 1rem;
}

.admin-card__desc {
  margin: 0 0 14px;
  font-size: 0.82rem;
  color: var(--ink-soft);
}

.admin-card__desc--spaced {
  margin-top: 10px;
  margin-bottom: 0;
}

.admin-message {
  margin-top: 12px;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid transparent;
}

.admin-message--success {
  color: var(--good);
  background: var(--good-soft);
  border-color: rgba(43, 139, 100, 0.18);
}

.admin-message--error {
  color: var(--danger);
  background: var(--danger-soft);
  border-color: rgba(203, 97, 69, 0.2);
}

.admin-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.admin-field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.admin-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.admin-field__label {
  font-size: 0.74rem;
  font-weight: 600;
  color: var(--ink-soft);
}

.admin-field__input {
  height: 38px;
  padding: 0 12px;
  border: 1px solid rgba(34, 58, 53, 0.14);
  border-radius: 10px;
  background: rgba(255, 250, 244, 0.6);
  font-size: 0.84rem;
  color: var(--ink);
  font-family: inherit;
  outline: none;
  transition: border-color 160ms ease, box-shadow 160ms ease;
}

.admin-field__input:focus {
  border-color: var(--brand);
  box-shadow: 0 0 0 3px rgba(41, 107, 115, 0.12);
}

.admin-field__input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.admin-field__input--sm {
  height: 30px;
  padding: 0 8px;
  font-size: 0.74rem;
}

.admin-field__input--search {
  height: 34px;
  padding-left: 32px;
}

.admin-search {
  position: relative;
}

.admin-search svg {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--ink-soft);
}

.admin-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 18px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--brand), var(--brand-strong));
  color: white;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: transform 160ms ease, box-shadow 160ms ease;
  white-space: nowrap;
}

.admin-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(41, 107, 115, 0.25);
}

.admin-btn--sm {
  padding: 5px 12px;
  font-size: 0.74rem;
  border-radius: 8px;
}

.admin-btn--ghost {
  background: transparent;
  color: var(--ink-soft);
  border: 1px solid rgba(29, 47, 44, 0.12);
}

.admin-btn--ghost:hover {
  background: rgba(41, 107, 115, 0.06);
  box-shadow: none;
}

.admin-btn--danger {
  background: linear-gradient(135deg, var(--danger), #a73522);
}

.admin-btn--danger:hover {
  box-shadow: 0 6px 16px rgba(203, 97, 69, 0.24);
}

.admin-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.admin-notice {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-bottom: 12px;
  padding: 10px 12px;
  border: 1px solid rgba(41, 107, 115, 0.12);
  border-radius: 12px;
  background: rgba(41, 107, 115, 0.05);
  color: var(--ink-soft);
  font-size: 0.82rem;
}

.admin-notice strong {
  color: var(--brand);
}

.org-card {
  margin-bottom: 12px;
}

.upload-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px 16px;
  border: 2px dashed rgba(41, 107, 115, 0.2);
  border-radius: 14px;
  background: rgba(41, 107, 115, 0.03);
  color: var(--ink-soft);
  font-size: 0.84rem;
  cursor: pointer;
  transition: all 160ms ease;
}

.upload-zone:hover,
.upload-zone.is-dragover {
  border-color: var(--brand);
  background: rgba(41, 107, 115, 0.08);
}

.upload-zone__hint {
  font-size: 0.72rem;
  color: rgba(92, 110, 104, 0.6);
}

.admin-text-muted {
  color: var(--ink-soft);
}

.admin-text-success {
  color: var(--good);
  font-weight: 700;
}

.admin-text-warning {
  color: var(--warn);
  font-weight: 700;
}

.admin-text-danger {
  color: var(--danger);
  font-weight: 700;
}

.admin-field--import-target {
  min-width: 180px;
  flex: 0 1 220px;
}

.upload-result {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  padding: 8px 12px;
  border-radius: 10px;
  background: rgba(43, 139, 100, 0.08);
  font-size: 0.82rem;
}

.upload-result__name {
  flex: 1;
  font-weight: 500;
}

.upload-result__size {
  color: var(--ink-soft);
  font-size: 0.74rem;
}

.admin-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}

.admin-table th {
  padding: 10px 12px;
  text-align: left;
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--ink-soft);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border-bottom: 1px solid rgba(29, 47, 44, 0.08);
  background: rgba(41, 107, 115, 0.04);
}

.admin-table td {
  padding: 10px 12px;
  border-bottom: 1px solid rgba(29, 47, 44, 0.06);
}

.admin-table__loading {
  text-align: center;
  color: var(--ink-soft);
}

.admin-table tr:hover td {
  background: rgba(41, 107, 115, 0.04);
}

.admin-table__row--fresh td {
  background: rgba(43, 139, 100, 0.08);
}

.admin-tag {
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 600;
}

.admin-tag--success {
  background: rgba(43, 139, 100, 0.12);
  color: var(--good);
}

.admin-tag--warn {
  background: rgba(210, 140, 40, 0.12);
  color: var(--warn);
}

.admin-tag--info {
  background: rgba(41, 107, 115, 0.1);
  color: var(--brand);
}

.admin-tag--muted {
  background: rgba(29, 47, 44, 0.06);
  color: var(--ink-soft);
}

.admin-link-btn {
  background: none;
  border: none;
  color: var(--brand);
  font-size: 0.78rem;
  font-weight: 500;
  cursor: pointer;
  padding: 2px 4px;
  font-family: inherit;
}

.admin-link-btn:hover {
  text-decoration: underline;
}

.admin-link-btn--danger {
  color: var(--danger);
}

.admin-row-actions {
  display: flex;
  gap: 8px;
}

.admin-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 14px 0 0;
  border-top: 1px solid rgba(29, 47, 44, 0.06);
  margin-top: 4px;
}

.admin-pagination__info {
  font-size: 0.78rem;
  color: var(--ink-soft);
}

.admin-muted-dash {
  color: rgba(92, 110, 104, 0.55);
}

.admin-pagination button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.center-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}




.center-header__left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  flex-wrap: wrap;
}

.center-header h3 {
  display: inline;
  margin-right: 0;
}

.collapse-arrow {
  flex-shrink: 0;
  color: var(--ink-soft);
  transition: transform 200ms ease;
}

.collapse-arrow.is-collapsed {
  transform: rotate(-90deg);
}

.center-actions {
  display: flex;
  gap: 8px;
}

.center-members {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.center-members__empty {
  padding: 12px;
  text-align: center;
  font-size: 0.78rem;
  color: var(--ink-soft);
  opacity: 0.6;
}

.upload-failures {
  margin-top: 14px;
  padding: 14px;
  border: 1px solid rgba(212, 87, 87, 0.18);
  border-radius: 16px;
  background: rgba(212, 87, 87, 0.05);
}

.upload-failures__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.upload-failures__header h4 {
  margin: 0;
  font-size: 0.96rem;
  color: var(--danger);
}

.upload-failures__header span {
  font-size: 0.78rem;
  color: var(--danger);
}

.upload-failures__header .admin-link-btn {
  flex: 0 0 auto;
}

.upload-failure-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 10px 0 12px;
}

.upload-failure-summary--modal {
  margin-top: 14px;
}

.upload-failure-summary__pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 999px;
  background: rgba(255, 250, 244, 0.76);
  border: 1px solid rgba(212, 87, 87, 0.16);
  color: var(--danger);
  font-size: 0.74rem;
  font-weight: 600;
}

.upload-failure-summary__pill strong {
  font-family: "Bahnschrift", "DIN Alternate", "Arial Narrow", sans-serif;
}

.upload-failures__table-wrap {
  max-height: 360px;
  overflow-x: auto;
  overflow-y: auto;
  border: 1px solid rgba(29, 47, 44, 0.06);
  border-radius: 10px;
  background: rgba(255, 250, 244, 0.55);
}

.upload-failures__table {
  min-width: 860px;
}

.upload-failures__table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--bg-strong);
}

.upload-failures__reason {
  color: var(--danger);
  font-weight: 600;
  white-space: nowrap;
}

.admin-modal--wide {
  width: min(1080px, calc(100vw - 32px));
  max-height: calc(100vh - 48px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.admin-modal__sub {
  margin: 4px 0 0;
  color: var(--ink-soft);
  font-size: 0.78rem;
}

.import-detail-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-top: 12px;
}

.import-detail-summary div {
  padding: 10px 12px;
  border: 1px solid rgba(29, 47, 44, 0.08);
  border-radius: 12px;
  background: rgba(41, 107, 115, 0.04);
}

.import-detail-summary span {
  display: block;
  color: var(--ink-soft);
  font-size: 0.72rem;
}

.import-detail-summary strong {
  display: block;
  margin-top: 4px;
  color: var(--ink);
  font-family: "Bahnschrift", "DIN Alternate", "Arial Narrow", sans-serif;
  font-size: 1.1rem;
}

.import-detail-table {
  margin-top: 12px;
  max-height: min(520px, calc(100vh - 300px));
}

.group-section {
  margin-bottom: 12px;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid rgba(29, 47, 44, 0.06);
  background: rgba(41, 107, 115, 0.02);
}

.group-section:last-child {
  margin-bottom: 0;
}

.group-section--nested {
  margin-top: 10px;
  background: rgba(255, 250, 244, 0.52);
}

.dept-section {
  background: rgba(41, 107, 115, 0.035);
}

.dept-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(29, 47, 44, 0.06);
}

.dept-header > div {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.dept-header strong {
  color: var(--ink);
  font-size: 0.9rem;
}

.group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(29, 47, 44, 0.06);
}

.group-header__left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  flex-wrap: wrap;
}

.group-name {
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--ink);
}

.group-actions {
  display: flex;
  gap: 8px;
}

.center-member {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(29, 47, 44, 0.06);
  background: rgba(255, 250, 244, 0.6);
}

.center-member__info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.center-member__info strong {
  font-size: 0.88rem;
}

.center-member__info span {
  font-size: 0.74rem;
  color: var(--ink-soft);
}

.center-member__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.admin-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(29, 47, 44, 0.3);
  backdrop-filter: blur(4px);
}

.admin-modal {
  width: 100%;
  max-width: 440px;
  padding: 24px;
  background: var(--surface-strong);
  border-radius: 20px;
  box-shadow: 0 20px 48px rgba(37, 56, 50, 0.15);
}

.admin-modal--confirm {
  max-width: 420px;
}

.admin-confirm__text {
  margin: 0 0 18px;
  color: var(--ink-soft);
  line-height: 1.6;
  font-size: 0.9rem;
}

.admin-modal__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.admin-modal__header h3 {
  margin: 0;
  font-size: 1.1rem;
}

.admin-modal__close {
  background: none;
  border: none;
  font-size: 1.1rem;
  color: var(--ink-soft);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
}

.admin-modal__close:hover {
  background: rgba(29, 47, 44, 0.06);
}

.admin-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
}

@media (max-width: 860px) {
  .admin-shell {
    padding: 12px 12px 24px;
  }

  .admin-topbar {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 12px 14px;
  }

  .admin-tabs {
    flex-wrap: wrap;
  }

  .admin-tab {
    font-size: 0.78rem;
    padding: 8px 12px;
  }

  .admin-grid--2col {
    grid-template-columns: 1fr;
  }

  .admin-section__header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .admin-section__tools {
    width: 100%;
  }

  .admin-search {
    flex: 1;
  }

  .admin-table {
    font-size: 0.74rem;
  }

  .admin-table th,
  .admin-table td {
    padding: 8px 6px;
  }

  .center-member {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .group-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .center-member__actions {
    width: 100%;
    justify-content: flex-end;
  }

  .admin-row-actions {
    flex-wrap: wrap;
    gap: 4px;
  }

  .dept-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .admin-modal {
    margin: 16px;
    max-width: none;
  }
}
</style>
